<template>
  <nav class="cases-navigation" data-cy="cases-navigation-bar">
    <div class="cases-navigation__items d-flex flex-wrap">
      <div v-if="!hideSettings" class="cases-navigation__item cases-navigation__item--icon" :class="{ 'active active--filters': filtersOpened }" @click="toggleFilters" data-cy="case-filters"><i class="pi pi-sliders-h"/></div>
      <div v-for="item in navigationItems" class="cases-navigation__item" 
           :class="[{'active': state.activeItem === item.name, 'awqaf-active': state.activeItem === item.name && (hideSettings)}, `item-${item.name}`]" 
           @click="itemClicked(item.name)" :data-cy="item.name+'-tab'">
        <div class="cases-navigation__item-content">
           <span class="cases-navigation__label" :title="item.label" ref="labelRefs">{{item.label}}</span>
           <img 
             v-if="item.iconName && !hideSettings"
             :src="hideSettings? item.iconName : state.activeItem === item.name 
               ? require(`@/assets/icons/filter-${item.iconName}-icon-active.svg`)
               : require(`@/assets/icons/filter-${item.iconName}-icon-non-active.svg`)" 
             class="cases-navigation__icon"
             :alt="item.label"
           />
           <img v-else-if="hideSettings"
             :src="state.activeItem === item.name ? item.iconActive : item.iconInactive" 
             class="cases-navigation__icon"
             :alt="item.label"/>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { onMounted, reactive, toRefs, watch } from "vue";

const props = defineProps({
  navigationItems: {
    type: Object,
    required: true
  },
  filtersOpened: {
    type: Boolean,
    required: false,
    default: false
  },
  hideSettings: {
    type: Boolean,
    required: false,
    default: false
  }
})

const state = reactive({
  activeItem: null,
})

const emit = defineEmits(["itemClicked", "toggleFilters"]);
const { navigationItems } = toRefs(props);

watch(() => props.navigationItems, (newItems) => {
  if (newItems && newItems.length > 0) {
    state.activeItem = newItems[0].name;
  }
}, { deep: true });

onMounted(() => {
  state.activeItem = props.navigationItems?.[0].name
})

const itemClicked = (name) => {
  state.activeItem = name;
  emit('itemClicked', name);
}
const toggleFilters = () => {
  if(state.activeItem !== 'cases') {
    emit('itemClicked', 'cases');
  }
  state.activeItem = 'cases';
  emit('toggleFilters');
}
</script>

<style lang="scss">
@import "@/styles/variables";
@import "@/styles/abstracts/mixins";

.cases-navigation {
  width: 100%;
  margin: -40px auto 0 auto;
  position: relative;
  z-index: 10;
  border-bottom: unset;

  &__items {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: 1065px;
    margin: auto;
    border: unset; 
    border-radius: 12px; 
    background-color: $color-background-filters; 
    overflow: hidden;
    gap: 10px; 
    padding: 10px; 
  }

  &__item {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 318px;
    max-width: 318px;
    flex: 1;
    padding: 15px 20px;
    cursor: pointer;
    color: $color-font-lighter;
    font-weight: 600;
    transition: all 0.3s ease;
    background-color: $color-tab-inactive; 
    border-radius: 8px; 
    
    &::after {
      display: none;
    }

    .cases-navigation__item-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      max-width: 100%; 
      overflow: hidden; 
    }

    .cases-navigation__label {
       white-space: nowrap;
       overflow: hidden;
       text-overflow: ellipsis;
       flex: 1; 
       min-width: 0; 
       text-align: right; 
       margin-left: 10px; 
    }

    .cases-navigation__icon {
      width: 24px;
      height:28px;
      flex-shrink: 0; 
    }

    i.pi {
      font-size: 24px;
      color: $color-primary; 
      transition: color 0.3s ease;
      flex-shrink: 0; 
    }
    &--icon {
      min-width: 60px;
      width: 60px; 
      flex: 0 0 60px;
      padding: 15px; 
      background-color: transparent; 
      border: 1px solid $color-tab-cases-start; 
      color: $color-tab-cases-start;
    }

    &:hover {
      background-color: darken($color-tab-inactive, 5%); 
    }
    
    &--icon:hover {
       background-color: rgba($color-tab-cases-start, 0.1);
    }

    &.active {
      background: linear-gradient(to right, $color-tab-cases-end, $color-tab-cases-start); 
      color: $color-white !important;

      i.pi {
        color: $color-white !important; 
      }
      
      &.cases-navigation__item--icon {
          background: $color-tab-cases-start; 
          border: 1px solid transparent; 
          pointer-events: auto !important; 
      }
    }

    &.active.awqaf-active {
        background: linear-gradient(to right, $color-awqaf-light, $color-awqaf-dark) !important;
    }
  }

  @include respond('lg') {
    width: 98%; 
    margin-top: 50px;
    &__item {
      padding: 14px 10px;
      font-size: 14px;
      i.pi {
        font-size: 21px;
      }
    }
  }
  @include respond('md') {
    &__items {
        flex-wrap: wrap;
        gap: 10px;
        padding: 5px;
        width: 98%;
    }
    &__item {
      font-size: 12px;
      padding: 14px 5px;
      flex: 0 0 calc(50% - 5px);
      min-width: calc(50% - 5px);
      max-width: calc(50% - 5px);
      
      &--icon {
        flex: 0 0 calc(50% - 5px);
        min-width: calc(50% - 5px);
        max-width: calc(50% - 5px);
        width: auto;
        order: 4;
      }
      i.pi {
        font-size: 18px;
      }
      .cases-navigation__item-content {
         // Spacing
      }
      .cases-navigation__label {
         display: block; 
      }

      &.item-cases { order: 1; }
      &.item-campaigns { order: 2; }
      &.item-markItClose { order: 3; }
      
      &:not(.cases-navigation__item--icon) {
        padding-left: 20px;
        padding-right: 20px;
      }
    }
  }
  
  @include respond('sm') {
      &__items {
          gap: 5px;
          padding: 5px;
      }
      &__item {
          padding: 10px 5px;
          font-size: 11px;
          
          &:not(.cases-navigation__item--icon) {
            padding-left: 15px;
            padding-right: 15px;
          }
      }
  }
}
</style>
